import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import WpDynamicTable from "../../Components/WhatsappTable/WpDynamicTable";
import {
  addSlab,
  DeleteSlab,
  fetchSlabList,
  UpdateSlab,
} from "../../Redux/WhatsappSlice/Slab";
import Loader from "../../Components/Loader/Loader";

const Add_Slab = () => {
  // Format ISO date to yyyy-mm-dd
  const formatDateTime = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear(); // Get the full 4-digit year
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const dispatch = useDispatch();

  const { SlabDataList } = useSelector((state) => state.Slab);

  const [formData, setFormData] = useState({
    WhaSlabName: "",
    WhaWEF: "",
  });

  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    dispatch(fetchSlabList());
  }, [dispatch]);

  // Form validation
  const validate = () => {
    const newErrors = {};
    const today = new Date();
    const selectedDate = new Date(formData.WhaWEF);

    if (!formData.WhaSlabName.trim()) {
      newErrors.WhaSlabName = "Slab Name is required";
    }

    if (!formData.WhaWEF) {
      newErrors.WhaWEF = "Effective Date is required";
    } else if (selectedDate < today.setHours(0, 0, 0, 0)) {
      newErrors.WhaWEF = "Effective Date cannot be in the past";
    }

    return newErrors;
  };

  // const handleEdit = (row) => {
  //   setIsEditMode(true);
  //   setEditingId(row.MainId);

  //   const formattedDate = formatDateTime(row.WhaWEF);

  //   setFormData({
  //     WhaSlabName: row.WhaSlabName,
  //     WhaWEF: formattedDate, // Set full formatted string
  //   });
  // };

  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditingId(row.MainId);

    //    using the unique ID (MainId) from the table row.
    const originalRowData = SlabDataList?.find(
      (item) => item._id === row.MainId
    );

    // 2. Add a check to ensure the data was found.
    if (!originalRowData) {
      return;
    }

    // 3. Use the original, unformatted WhaWEF date from the found object.
    const originalWhaWEF = originalRowData.WhaWEF;

    setFormData({
      WhaSlabName: originalRowData?.WhaSlabName || "",
      WhaWEF: originalWhaWEF
        ? new Date(originalWhaWEF).toISOString().split("T")[0]
        : "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDelete = async (row) => {
    const isConfirmed = window.confirm(
      `Are you sure you want to delete this Slab?`
    );
    if (!isConfirmed) return;

    try {
      await dispatch(DeleteSlab({ id: row.MainId })).unwrap();
      toast.success("Slab deleted successfully!", {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
      });
      dispatch(fetchSlabList()); // refresh list
    } catch (error) {
      toast.error(`Failed to delete Slab: ${error}`, {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      try {
        // Get only the date (from input)
        const selectedDateOnly = new Date(formData.WhaWEF.split(" ")[0]);

        // Get current time
        const now = new Date();
        selectedDateOnly.setHours(now.getHours());
        selectedDateOnly.setMinutes(now.getMinutes());

        const formattedDate = formatDateTime(selectedDateOnly);

        const payload = {
          ...formData,
          WhaWEF: formattedDate,
        };

        if (isEditMode) {
          await dispatch(
            UpdateSlab({ id: editingId, values: payload })
          ).unwrap();
          toast.success("Slab updated successfully!");
        } else {
          await dispatch(addSlab({ values: payload })).unwrap();
          toast.success("Slab added successfully!");
        }

        setFormData({ WhaSlabName: "", WhaWEF: "" });
        setIsEditMode(false);
        setEditingId(null);
        dispatch(fetchSlabList());
      } catch (error) {
        toast.error(`Failed to ${isEditMode ? "update" : "add"} Slab`);
      }
    } else {
      toast.error("Please fix validation errors.");
    }
  };

  const columns = [
    { field: "id", headerName: "No", flex: 1 },
    { field: "WhaSlabName", headerName: "Slab Name", flex: 1 },
    { field: "WhaWEF", headerName: "Version Date", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <div className="flex space-x-2 justify-center">
          <button
            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
            onClick={() => handleEdit(params.row)}
          >
            Update
          </button>
          <button
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
            onClick={() => handleDelete(params.row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const rows =
    SlabDataList?.map((el, index) => ({
      id: index + 1,
      MainId: el._id,
      WhaSlabName: el?.WhaSlabName,
      WhaWEF: formatDisplayDate(el?.WhaWEF),
    })) || [];

  if (!SlabDataList ) {
    return <Loader />;
  }

  return (
    <>
      <div className="">
        <div className="title">
          <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
            {isEditMode ? "Update Slab" : "Add Slab"}
          </h3>
        </div>

        <div className="Form_bg_color bg-white shadow-sm rounded-xl mt-4">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-12 gap-4 p-3 rounded-2xl"
          >
            {/* Slab Name Input */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="my-2 font-semibold">Slab Name</h5>
              <input
                type="text"
                name="WhaSlabName"
                placeholder="Enter Slab Name"
                value={formData.WhaSlabName}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
              {errors.WhaSlabName && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.WhaSlabName}
                </p>
              )}
            </div>

            {/* Effective Date Input */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="my-2 font-semibold">Effective Date</h5>
              <input
                type="date"
                name="WhaWEF"
                value={formData.WhaWEF?.split(" ")[0] || ""}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
              {errors.WhaWEF && (
                <p className="text-red-500 text-sm mt-1">{errors.WhaWEF}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="submit_Form col-span-12 flex justify-center space-x-4">
              <button type="submit" className="All_button">
                {isEditMode ? "Update" : "Submit"}
              </button>
              {isEditMode && (
                <button
                  type="button"
                  onClick={() => {
                    setIsEditMode(false);
                    setEditingId(null);
                    setFormData({ WhaSlabName: "", WhaWEF: "" });
                  }}
                  className="All_button bg-gray-500 hover:bg-gray-600"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="px-2 py-6">
          <div className="title">
            <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
              View Slab
            </h3>
          </div>
          <div className="rounded-lg overflow-x-auto mt-5">
            <WpDynamicTable columns={columns} rows={rows} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Add_Slab;
